import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle, XCircle, ArrowsClockwise, Heartbeat } from "@phosphor-icons/react";
import { BrandMark } from "@/components/Brand";
import useSeo from "@/lib/useSeo";

const LABELS = {
  api: "FastAPI backend",
  mongo: "MongoDB database",
  stripe: "Stripe billing key",
  emergent_llm: "ARISSAI AI engine",
  jwt_secret: "JWT secret",
  resend: "Resend email key",
  scheduler: "Weekly Brief scheduler",
};

const ORDER = ["api", "mongo", "stripe", "emergent_llm", "jwt_secret", "resend", "scheduler"];

export default function Status() {
  useSeo({
    title: "Service status · ARISSAI® · Live uptime, AI models & integrations",
    description: "Real-time service health for ARISSAI® — API, scheduler, AI providers (Claude Sonnet 4.5, GPT-5.2), Stripe billing and Resend email. Incident history and current uptime.",
    path: "/status",
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get("/status");
      setData(data);
    } catch (e) {
      setError(e?.message || "Failed to reach API");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  const overall = data?.status;
  const overallTone = overall === "ok"
    ? { bg: "#DCFCE7", text: "#16A34A", label: "All systems operational" }
    : overall === "degraded"
    ? { bg: "#FFE5E3", text: "#FF3B30", label: "Degraded — see details" }
    : { bg: "#F4F4F5", text: "#52525B", label: "Checking…" };

  return (
    <div className="min-h-screen bg-zinc-100">
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <BrandMark size={36} testid="status-brand" tagline="System Status" />
          <Link to="/" className="text-sm font-medium hover:text-zinc-950" data-testid="status-home-link">← Home</Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 sm:px-10 py-12">
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500">// Status</div>
        <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-2">System health</h1>
        <p className="text-zinc-600 mt-2">Auto-refreshes every 30 seconds.</p>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-3 bg-white border border-zinc-200 p-5">
          <div className="flex items-center gap-3">
            <Heartbeat size={22} weight="duotone" style={{ color: overallTone.text }} />
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Overall</div>
              <div className="font-display font-bold text-xl" style={{ color: overallTone.text }} data-testid="status-overall">{overallTone.label}</div>
            </div>
          </div>
          <button onClick={load} disabled={loading}
            className="inline-flex items-center gap-2 h-10 px-4 border border-zinc-300 text-xs font-bold hover:bg-zinc-100 disabled:opacity-50"
            data-testid="status-refresh-button">
            <ArrowsClockwise size={12} weight="bold" /> {loading ? "Checking…" : "Refresh now"}
          </button>
        </div>

        {error && (
          <div className="mt-4 bg-[#FFE5E3] border border-[#FF3B30] p-4 text-sm text-[#FF3B30]" data-testid="status-error">
            Could not reach status API: {error}
          </div>
        )}

        <div className="mt-6 divide-y divide-zinc-200 border border-zinc-200 bg-white">
          {ORDER.map((key) => {
            const check = data?.checks?.[key];
            const ok = check?.ok;
            const Icon = ok ? CheckCircle : XCircle;
            const color = ok ? "#16A34A" : "#FF3B30";
            return (
              <div key={key} className="p-4 flex items-start justify-between gap-4" data-testid={`status-row-${key}`}>
                <div className="flex items-start gap-3 min-w-0">
                  <Icon size={20} weight="fill" style={{ color }} className="mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-display font-bold text-base">{LABELS[key] || key}</div>
                    <div className="text-xs text-zinc-600 mt-0.5">{check?.detail || "—"}</div>
                    {check?.jobs?.length > 0 && (
                      <div className="text-[10px] font-mono text-zinc-500 mt-1">jobs: {check.jobs.join(", ")}</div>
                    )}
                  </div>
                </div>
                <span className="text-[10px] tracking-[0.2em] uppercase font-bold px-2 h-6 inline-flex items-center" style={{ background: ok ? "#DCFCE7" : "#FFE5E3", color }}>
                  {ok ? "ok" : "down"}
                </span>
              </div>
            );
          })}
        </div>

        <div className="mt-6 text-xs text-zinc-500">
          Last checked: <span className="font-mono">{data?.checked_at || "—"}</span>
        </div>
      </main>
    </div>
  );
}
